(function () {
  'use strict';

  if (customElements.get('bundle-tiers')) return;

  /**
   * <bundle-tiers data-form-id="product-form-...">
   *
   * Quantity-tier selector for the main product form. Clicking a tier writes
   * the tier's quantity into the real product form's quantity input and
   * dispatches input/change so the theme's own components react. It never
   * posts to /cart/add and owns no submit button.
   *
   * Prices per tier are re-computed from a variant→price payload whenever the
   * shopper changes variant, so a $69 → $79 variant swap re-prices every tier.
   */
  class BundleTiers extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.syncFromInput = this.syncFromInput.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.tiers = Array.prototype.slice.call(this.querySelectorAll('.bundle-tier'));
      if (this.tiers.length === 0) return;

      this.priceData = this.readPriceData();
      this.addEventListener('click', this.onClick);

      this.quantityInput = this.findQuantityInput();
      if (this.quantityInput) {
        this.quantityInput.addEventListener('input', this.syncFromInput);
        this.quantityInput.addEventListener('change', this.syncFromInput);
        this.syncFromInput();
      }

      // Re-price when the variant changes. Dawn dispatches change on the
      // form's id input; a MutationObserver catches DOM-swapped buy boxes.
      this.form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
      this.variantInput = this.findVariantInput();
      if (this.variantInput) {
        this.variantInput.addEventListener('change', this.onVariantChange);
      }
      this.productInfo = this.closest('product-info') || this.closest('.shopify-section');
      if (this.productInfo && 'MutationObserver' in window) {
        // rAF-debounced: editor re-renders fire storms of mutations, and our own
        // price writes land inside the observed tree — without the debounce +
        // write-only-on-change guards below this would loop forever and freeze
        // the theme editor's preview iframe.
        var self = this;
        this.observer = new MutationObserver(function () {
          if (self.rafId) return;
          self.rafId = requestAnimationFrame(function () {
            self.rafId = null;
            self.onVariantChange();
          });
        });
        this.observer.observe(this.productInfo, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.quantityInput) {
        this.quantityInput.removeEventListener('input', this.syncFromInput);
        this.quantityInput.removeEventListener('change', this.syncFromInput);
        this.quantityInput = null;
      }
      if (this.variantInput) {
        this.variantInput.removeEventListener('change', this.onVariantChange);
        this.variantInput = null;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    readPriceData() {
      var el = this.querySelector('.bundle-tiers__data');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    findVariantInput() {
      var scope = this.form || this.closest('.shopify-section') || document;
      return scope.querySelector('input[name="id"], select[name="id"]');
    }

    findQuantityInput() {
      var formId = this.dataset.formId || '';
      if (formId) {
        var escapedId =
          window.CSS && typeof CSS.escape === 'function'
            ? CSS.escape(formId)
            : formId.replace(/["\\]/g, '\\$&');
        var input = document.querySelector('input[name="quantity"][form="' + escapedId + '"]');
        if (input) return input;
        var form = document.getElementById(formId);
        if (form && form.elements) {
          var named = form.elements.namedItem('quantity');
          if (named && named.tagName === 'INPUT') return named;
        }
      }
      var scope = this.closest('.shopify-section') || document;
      var found = scope.querySelector('input[name="quantity"]');
      if (found) return found;
      return this.createHiddenQuantityInput();
    }

    // The quantity_selector block is optional (merchants hide it when the
    // tiers replace it visually). The form still needs a quantity field for
    // tiers to write into, so fall back to a hidden input on the real form.
    createHiddenQuantityInput() {
      var formId = this.dataset.formId || '';
      var form = formId ? document.getElementById(formId) : null;
      if (!form) return null;
      var existing = form.querySelector('input[name="quantity"]');
      if (existing) return existing;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      input.value = '1';
      form.appendChild(input);
      return input;
    }

    onClick(event) {
      var tier = event.target.closest('.bundle-tier');
      if (!tier || !this.contains(tier)) return;

      var quantity = parseInt(tier.dataset.quantity, 10);
      if (isNaN(quantity) || quantity < 1) return;

      var input = this.resolveQuantityInput();
      if (!input) return;

      var min = parseInt(input.min, 10);
      var max = parseInt(input.max, 10);
      if (!isNaN(min) && min > 0) quantity = Math.max(min, quantity);
      if (!isNaN(max) && max > 0) quantity = Math.min(max, quantity);

      input.value = String(quantity);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncFromInput();
    }

    // Variant changes can re-render the buy box and detach the input we
    // captured on connect; re-resolve whenever the cached one left the DOM.
    resolveQuantityInput() {
      if (!this.quantityInput || !this.quantityInput.isConnected) {
        this.quantityInput = this.findQuantityInput();
      }
      return this.quantityInput;
    }

    syncFromInput() {
      var input = this.resolveQuantityInput();
      if (!input) return;
      var value = parseInt(input.value, 10);
      this.tiers.forEach(function (tier) {
        var isMatch = parseInt(tier.dataset.quantity, 10) === value;
        tier.setAttribute('aria-checked', isMatch ? 'true' : 'false');
      });
    }

    onVariantChange() {
      if (!this.priceData) return;
      // Re-resolve the variant input in case the buy box was re-rendered.
      var vinput = this.findVariantInput();
      if (vinput && vinput !== this.variantInput) {
        if (this.variantInput) this.variantInput.removeEventListener('change', this.onVariantChange);
        this.variantInput = vinput;
        this.variantInput.addEventListener('change', this.onVariantChange);
      }
      var id = vinput && vinput.value;
      var rows = id && this.priceData[id];
      if (!rows) return;

      var byQty = {};
      rows.forEach(function (r) {
        byQty[r.q] = r;
      });

      // Write only when the value actually changed — our writes land inside the
      // observed tree, so unconditional writes would re-trigger the observer in
      // an endless loop (freezing the theme editor preview).
      var setText = function (el, value) {
        if (el && el.textContent !== value) el.textContent = value;
      };
      this.tiers.forEach(function (tier) {
        var q = parseInt(tier.dataset.quantity, 10);
        var row = byQty[q];
        if (!row) return;
        setText(tier.querySelector('[data-tier-net]'), row.net);
        setText(tier.querySelector('[data-tier-gross]'), row.gross);
        setText(tier.querySelector('[data-tier-per]'), row.per);
      });
    }
  }

  customElements.define('bundle-tiers', BundleTiers);
})();
